/*
 * Sticky add-to-cart bar (mobile).
 *
 * Visibility rule: shown whenever the main product form's buttons are NOT in
 * the viewport, hidden as soon as they scroll back into view.
 *
 * The bar owns no cart state of its own:
 *   - the quantity stepper proxies the main <quantity-input> when the quantity
 *     selector block is present, so the theme's quantity rules stay
 *     authoritative. When that block is absent the stepper runs standalone off
 *     the min/max/step Liquid rendered from the variant's quantity rules, and
 *     injects its own hidden quantity field into the form before submitting;
 *   - ADD TO CART forwards the click to the real submit button, so
 *     product-form.js handles the fetch, errors and cart-drawer render;
 *   - BUY NOW native-submits the main form with return_to=/checkout. Calling
 *     form.submit() does not fire the submit event, so it deliberately bypasses
 *     the AJAX handler while still posting every field the form carries
 *     (variant, quantity, properties, selling plan).
 */
if (!customElements.get('sticky-add-to-cart')) {
  customElements.define(
    'sticky-add-to-cart',
    class StickyAddToCart extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.mobileQuery = window.matchMedia('(max-width: 749px)');

        this.addButton = this.querySelector('[data-sticky-atc-add]');
        this.addButtonText = this.querySelector('[data-sticky-atc-add-text]');
        this.buyButton = this.querySelector('[data-sticky-atc-buy]');
        this.quantityInput = this.querySelector('[data-sticky-atc-qty-input]');

        this.onAddClick = this.onAddClick.bind(this);
        this.onBuyClick = this.onBuyClick.bind(this);
        this.onQuantityStep = this.onQuantityStep.bind(this);
        this.onQuantityTyped = this.onQuantityTyped.bind(this);
        this.onBreakpointChange = this.onBreakpointChange.bind(this);

        this.addButton?.addEventListener('click', this.onAddClick);
        this.buyButton?.addEventListener('click', this.onBuyClick);
        this.quantityInput?.addEventListener('change', this.onQuantityTyped);
        this.querySelectorAll('[data-sticky-atc-qty]').forEach((button) =>
          button.addEventListener('click', this.onQuantityStep)
        );
        this.mobileQuery.addEventListener('change', this.onBreakpointChange);

        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (event?.data?.sectionId !== this.sectionId) return;
          this.sync();
        });

        this.sync();
        this.onBreakpointChange();
      }

      disconnectedCallback() {
        this.teardownObservers();
        this.addButton?.removeEventListener('click', this.onAddClick);
        this.buyButton?.removeEventListener('click', this.onBuyClick);
        this.quantityInput?.removeEventListener('change', this.onQuantityTyped);
        this.querySelectorAll('[data-sticky-atc-qty]').forEach((button) =>
          button.removeEventListener('click', this.onQuantityStep)
        );
        this.mobileQuery.removeEventListener('change', this.onBreakpointChange);
        this.variantChangeUnsubscriber?.();
      }

      /* These are re-queried on every use: product-info.js can replace parts of
         the info column when the variant changes. */
      get mainSubmitButton() {
        return document.getElementById(`ProductSubmitButton-${this.sectionId}`);
      }

      get mainQuantityInput() {
        return document.getElementById(`Quantity-${this.sectionId}`);
      }

      get mainForm() {
        return document.getElementById(`product-form-${this.sectionId}`);
      }

      get observedTarget() {
        return this.mainSubmitButton?.closest('.product-form__buttons');
      }

      onBreakpointChange() {
        if (this.mobileQuery.matches) {
          this.setupObservers();
        } else {
          this.teardownObservers();
          this.toggle(false);
        }
      }

      setupObservers() {
        const target = this.observedTarget;
        if (!target || this.intersectionObserver) return;

        this.intersectionObserver = new IntersectionObserver(
          (entries) => entries.forEach((entry) => this.toggle(!entry.isIntersecting)),
          { threshold: 0 }
        );
        this.intersectionObserver.observe(target);

        /* Mirror state changes made by product-form.js / product-info.js rather
           than recomputing availability here. */
        this.mutationObserver = new MutationObserver(() => this.sync());
        this.mutationObserver.observe(target, {
          attributes: true,
          childList: true,
          subtree: true,
          attributeFilter: ['disabled', 'aria-disabled', 'class'],
        });

        const quantity = this.mainQuantityInput;
        if (quantity) {
          this.quantityObserver = new MutationObserver(() => this.syncQuantity());
          this.quantityObserver.observe(quantity, { attributes: true, attributeFilter: ['value', 'min', 'max'] });
          quantity.addEventListener('change', this.onQuantityTyped);
        }
      }

      teardownObservers() {
        this.intersectionObserver?.disconnect();
        this.mutationObserver?.disconnect();
        this.quantityObserver?.disconnect();
        this.intersectionObserver = null;
        this.mutationObserver = null;
        this.quantityObserver = null;
      }

      toggle(visible) {
        this.classList.toggle('sticky-atc--visible', visible);
        this.toggleAttribute('inert', !visible);
        document.documentElement.classList.toggle('sticky-atc-is-visible', visible);
      }

      onAddClick(event) {
        event.preventDefault();
        const submitButton = this.mainSubmitButton;
        if (!submitButton || submitButton.disabled) return;
        this.applyStandaloneQuantity();
        submitButton.click();
      }

      onBuyClick(event) {
        event.preventDefault();
        const form = this.mainForm;
        const submitButton = this.mainSubmitButton;
        if (!form || !submitButton || submitButton.disabled) return;

        this.applyStandaloneQuantity();

        let returnTo = form.querySelector('input[name="return_to"]');
        if (!returnTo) {
          returnTo = document.createElement('input');
          returnTo.type = 'hidden';
          returnTo.name = 'return_to';
          form.appendChild(returnTo);
        }
        returnTo.value = '/checkout';

        this.buyButton.classList.add('loading');
        form.submit();
      }

      /* Proxy the theme's own stepper buttons when they exist, so quantity rules
         stay authoritative; otherwise step our own value within the rendered
         min/max/step. */
      onQuantityStep(event) {
        const direction = event.currentTarget.dataset.stickyAtcQty;
        const mainButton = this.mainQuantityInput
          ?.closest('quantity-input')
          ?.querySelector(`.quantity__button[name="${direction}"]`);

        if (mainButton) {
          mainButton.click();
          this.syncQuantity();
          return;
        }

        const step = parseInt(this.quantityInput.step, 10) || 1;
        const delta = direction === 'plus' ? step : -step;
        this.setStandaloneQuantity((parseInt(this.quantityInput.value, 10) || 1) + delta);
      }

      onQuantityTyped(event) {
        const main = this.mainQuantityInput;

        if (!main) {
          this.setStandaloneQuantity(parseInt(this.quantityInput.value, 10));
          return;
        }

        if (event.target === main) {
          this.syncQuantity();
          return;
        }

        main.value = this.quantityInput.value;
        main.dispatchEvent(new Event('change', { bubbles: true }));
        this.syncQuantity();
      }

      setStandaloneQuantity(value) {
        const min = parseInt(this.quantityInput.min, 10) || 1;
        const max = this.quantityInput.max ? parseInt(this.quantityInput.max, 10) : Infinity;
        this.quantityInput.value = Math.min(max, Math.max(min, Number.isNaN(value) ? min : value));
        this.updateStepperState();
      }

      syncQuantity() {
        const main = this.mainQuantityInput;
        if (!main || !this.quantityInput) return;
        this.quantityInput.value = main.value;
        this.quantityInput.min = main.min || 1;
        if (main.max) this.quantityInput.max = main.max;
        this.quantityInput.step = main.step || 1;
        this.updateStepperState();
      }

      updateStepperState() {
        if (!this.quantityInput) return;
        const value = parseInt(this.quantityInput.value, 10) || 1;
        const min = parseInt(this.quantityInput.min, 10) || 1;
        const max = this.quantityInput.max ? parseInt(this.quantityInput.max, 10) : Infinity;

        this.querySelector('[data-sticky-atc-qty="minus"]')?.toggleAttribute('disabled', value <= min);
        this.querySelector('[data-sticky-atc-qty="plus"]')?.toggleAttribute('disabled', value >= max);
      }

      /* With no quantity selector block on the page the form carries no quantity
         field, so contribute one. product-form.js reads the form via FormData,
         and the BUY NOW native submit posts it too. */
      applyStandaloneQuantity() {
        const form = this.mainForm;
        if (!form || this.mainQuantityInput) return;

        let input = form.querySelector('input[name="quantity"][data-sticky-atc-owned]');
        if (!input) {
          input = document.createElement('input');
          input.type = 'hidden';
          input.name = 'quantity';
          input.setAttribute('data-sticky-atc-owned', '');
          form.appendChild(input);
        }
        input.value = this.quantityInput.value;
      }

      sync() {
        const submitButton = this.mainSubmitButton;
        if (!submitButton) return;

        const disabled = submitButton.hasAttribute('disabled');
        const busy = submitButton.getAttribute('aria-disabled') === 'true';
        const loading = submitButton.classList.contains('loading');

        [this.addButton, this.buyButton].forEach((button) => {
          if (!button) return;
          button.disabled = disabled;
          button.setAttribute('aria-disabled', busy);
        });

        this.addButton?.classList.toggle('loading', loading);

        const label = submitButton.querySelector('span')?.textContent.trim();
        if (label && this.addButtonText) this.addButtonText.textContent = label;

        if (this.mainQuantityInput) {
          this.syncQuantity();
        } else {
          this.updateStepperState();
        }
      }
    }
  );
}
